import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StoreProvider } from './context/StoreContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import TryLanding from './pages/TryLanding';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import SalesPoint from './pages/SalesPoint';
import SalesHistory from './pages/SalesHistory';
import POS from './pages/POS';
import ImportInventory from './pages/ImportInventory';
import SmartPO from './pages/SmartPO';
import WeeklyReport from './pages/WeeklyReport';
import GSTReturn from './pages/GSTReturn';
import StaffManagement from './pages/StaffManagement';
import StaffTasks from './pages/StaffTasks';
import SupplierInvoices from './pages/SupplierInvoices';
import CustomerReminders from './pages/CustomerReminders';
import ShopifyIntegration from './pages/ShopifyIntegration';
import Suppliers from './pages/Suppliers';
import StockTransfer from './pages/StockTransfer';
import TransferHistory from './pages/TransferHistory';
import StockCount from './pages/StockCount';
import StoreSettings from './pages/StoreSettings';
import HardwareSettings from './pages/HardwareSettings';
import TillRegister from './pages/TillRegister';
import Customers from './pages/Customers';
import QuickKeysSettings from './pages/QuickKeysSettings';
import StaffLogin from './pages/StaffLogin';
import ShortDatedStock from './pages/ShortDatedStock';
import InventoryHealth from './pages/InventoryHealth';
import SalesReport from './pages/SalesReport';
import OOSReport from './pages/OOSReport';
import CustomerOrders from './pages/CustomerOrders';
import CustomerRequests from './pages/CustomerRequests';
import StoreChat from './pages/StoreChat';
import NoticeBoard from './pages/NoticeBoard';
import ProtocolEditor from './pages/ProtocolEditor';
import StaffActivity from './pages/StaffActivity';
import ReceiveStock from './pages/ReceiveStock';
import ReceiveHistory from './pages/ReceiveHistory';
import StaffManual from './pages/StaffManual';
import MarketingEmail from './pages/MarketingEmail';
import WeeklyRoster from './pages/WeeklyRoster';
import TaskAccountability from './pages/TaskAccountability';
import ShopifyHealth from './pages/ShopifyHealth';
import PlatformAdmin from './pages/PlatformAdmin';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading, token, activeStaff, showStaffLogin, setShowStaffLogin, setActiveStaff, setStaffToken } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show staff login screen if store is logged in but no active staff
  // (This is optional - can be enabled by setting showStaffLogin to true)
  if (showStaffLogin && !activeStaff) {
    return (
      <StaffLogin 
        storeToken={token}
        onStaffLogin={(data) => {
          // Update context state directly with staff data
          if (setActiveStaff) setActiveStaff(data.staff);
          if (setStaffToken) setStaffToken(data.access_token);
          setShowStaffLogin(false);
        }}
        onBackToStoreLogin={() => setShowStaffLogin(false)}
      />
    );
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const inviteFor = new URLSearchParams(window.location.search).get('as');
  const otherAccount = !!(user && inviteFor && ![user.email, user.login_email].filter(Boolean).map(e => e.toLowerCase()).includes(inviteFor.toLowerCase()));

  // An invite link for someone else must never land in the signed-in account's store
  React.useEffect(() => {
    if (otherAccount) logout();
  }, [otherAccount, logout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user && !otherAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/try" element={<TryLanding />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pos" 
        element={
          <ProtectedRoute>
            <POS />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/import" 
        element={
          <ProtectedRoute>
            <ImportInventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales" 
        element={
          <ProtectedRoute>
            <SalesPoint />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <SalesHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/smart-po" 
        element={
          <ProtectedRoute>
            <SmartPO />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/receive-stock" 
        element={
          <ProtectedRoute>
            <ReceiveStock />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/receive-history" 
        element={
          <ProtectedRoute>
            <ReceiveHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/gst-return" 
        element={
          <ProtectedRoute>
            <GSTReturn />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <WeeklyReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff-tasks" 
        element={
          <ProtectedRoute>
            <StaffTasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/oos-report" 
        element={
          <ProtectedRoute>
            <OOSReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-orders" 
        element={
          <ProtectedRoute>
            <CustomerOrders view="to-order" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ordered-waiting" 
        element={
          <ProtectedRoute>
            <CustomerOrders view="ordered" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-requests" 
        element={
          <ProtectedRoute>
            <CustomerRequests />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/store-chat" 
        element={
          <ProtectedRoute>
            <StoreChat />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notice-board" 
        element={
          <ProtectedRoute>
            <NoticeBoard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/marketing" 
        element={
          <ProtectedRoute>
            <MarketingEmail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/weekly-roster" 
        element={
          <ProtectedRoute>
            <WeeklyRoster />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/task-accountability" 
        element={
          <ProtectedRoute>
            <TaskAccountability />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff" 
        element={
          <ProtectedRoute>
            <StaffManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff-activity" 
        element={
          <ProtectedRoute>
            <StaffActivity />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices" 
        element={
          <ProtectedRoute>
            <SupplierInvoices />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shopify-health" 
        element={
          <ProtectedRoute>
            <ShopifyHealth />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <PlatformAdmin />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reminders" 
        element={
          <ProtectedRoute>
            <CustomerReminders />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shopify" 
        element={
          <ProtectedRoute>
            <ShopifyIntegration />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ecommerce" 
        element={
          <ProtectedRoute>
            <ShopifyIntegration />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/suppliers" 
        element={
          <ProtectedRoute>
            <Suppliers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/stock-transfer" 
        element={
          <ProtectedRoute>
            <StockTransfer />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/transfer-history" 
        element={
          <ProtectedRoute>
            <TransferHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/stock-count" 
        element={
          <ProtectedRoute>
            <StockCount />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/store-settings" 
        element={
          <ProtectedRoute>
            <StoreSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/hardware" 
        element={
          <ProtectedRoute>
            <HardwareSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <StaffManual />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/till" 
        element={
          <ProtectedRoute>
            <TillRegister />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customers" 
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/quick-keys" 
        element={
          <ProtectedRoute>
            <QuickKeysSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/short-dated" 
        element={
          <ProtectedRoute>
            <ShortDatedStock />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory-health" 
        element={
          <ProtectedRoute>
            <InventoryHealth />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales-report" 
        element={
          <ProtectedRoute>
            <SalesReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff-login" 
        element={
          <StaffLogin />
        } 
      />
      <Route 
        path="/protocols" 
        element={
          <ProtectedRoute>
            <ProtocolEditor />
          </ProtectedRoute>
        } 
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
